'use client'

import { ChatChannelIcon } from '@/components/atendimento/chat-channel-icon'
import {
    createLead,
    CreateLeadData,
    getLeads,
    getWallets,
    Lead,
    LEAD_LIFECYCLE_LABELS,
    LEAD_SENTIMENT_LABELS,
    LEAD_SOURCE_LABELS,
    LEAD_STATUS_COLORS,
    LEAD_STATUS_LABELS,
    LEAD_TEMPERATURE_LABELS,
    LeadFilters,
    LeadLifecycleStatus,
    LeadSentimentType,
    LeadSource,
    LeadStatus,
    LeadTemperature,
} from '@/lib/api/sales'
import { getPipelines, SalesPipeline } from '@/lib/api/sales/pipelines-api'
import { SalesWallet } from '@/lib/api/sales/wallets-api'
import { getUsersByCompany } from '@/lib/api/users/users-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useUrlPage } from '@/lib/hooks/use-url-page'
import { useAuth } from '@/store/hooks'
import PaginationControls from '@/components/pagination/PaginationControls'
import { useEffect, useState } from 'react'
import {
    MdAdd,
    MdClear,
    MdClose,
    MdFilterList,
    MdLocalOffer,
    MdPerson,
    MdSearch,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

interface CompanyUser {
    id: number
    name: string
    email: string
}

const SENTIMENT_BG: Record<LeadSentimentType, string> = {
    INTERESTED: 'badge-info',
    VERY_SATISFIED: 'badge-success',
    SATISFIED: 'badge-success',
    NEUTRAL: 'badge-info',
    CURIOUS: 'badge-info',
    HESITANT: 'badge-warning',
    DISSATISFIED: 'badge-warning',
    FRUSTRATED: 'badge-warning',
    ANGRY: 'badge-error',
    URGENT: 'badge-secondary',
}

const TEMPERATURE_DOT: Record<LeadTemperature, string> = {
    HOT: 'bg-error',
    WARM: 'bg-orange-400',
    COLD: 'bg-info',
    FROZEN: 'bg-base-content/30',
}

export default function LeadsListPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { canCreate, canUpdate } = useResourcePermissions('sales', 'leads')

    const [loading, setLoading] = useState(true)
    const [leads, setLeads] = useState<Lead[]>([])
    const [totalLeads, setTotalLeads] = useState(0)
    const [loadingMore, setLoadingMore] = useState(false)
    const [page, setPage] = useUrlPage()
    const LIMIT = 50

    // Filters
    const [showFilters, setShowFilters] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [filterStatus, setFilterStatus] = useState<string>('')
    const [filterLifecycle, setFilterLifecycle] = useState<string>('')
    const [filterSentiment, setFilterSentiment] = useState<string>('')
    const [filterTemperature, setFilterTemperature] = useState<string>('')
    const [filterWalletId, setFilterWalletId] = useState<string>('')
    const [filterPipelineId, setFilterPipelineId] = useState<string>('')
    const [filterStageId, setFilterStageId] = useState<string>('')
    const [filterMinScore, setFilterMinScore] = useState('')
    const [filterMaxScore, setFilterMaxScore] = useState('')
    const [filterStartDate, setFilterStartDate] = useState('')
    const [filterEndDate, setFilterEndDate] = useState('')
    const [filterTags, setFilterTags] = useState('')

    // Reference data
    const [wallets, setWallets] = useState<SalesWallet[]>([])
    const [pipelines, setPipelines] = useState<SalesPipeline[]>([])
    const [companyUsers, setCompanyUsers] = useState<CompanyUser[]>([])

    // Create modal
    const [showCreateModal, setShowCreateModal] = useState(false)
    const [creating, setCreating] = useState(false)
    const [formName, setFormName] = useState('')
    const [formPhone, setFormPhone] = useState('')
    const [formEmail, setFormEmail] = useState('')
    const [formSource, setFormSource] = useState<LeadSource | ''>('')
    const [formLifecycle, setFormLifecycle] = useState<
        LeadLifecycleStatus | ''
    >('')
    const [formTemperature, setFormTemperature] = useState<
        LeadTemperature | ''
    >('')
    const [formHasBudget, setFormHasBudget] = useState(false)
    const [formIsDecisionMaker, setFormIsDecisionMaker] = useState(false)
    const [formHasNeed, setFormHasNeed] = useState(false)
    const [formExpectedTimeline, setFormExpectedTimeline] = useState('')
    const [formUtmSource, setFormUtmSource] = useState('')
    const [formUtmMedium, setFormUtmMedium] = useState('')
    const [formUtmCampaign, setFormUtmCampaign] = useState('')
    const [formUtmTerm, setFormUtmTerm] = useState('')
    const [formUtmContent, setFormUtmContent] = useState('')
    const [formCompanyName, setFormCompanyName] = useState('')
    const [formPosition, setFormPosition] = useState('')
    const [formWebsite, setFormWebsite] = useState('')
    const [formTags, setFormTags] = useState('')
    const [formWalletId, setFormWalletId] = useState<string>('')
    const [formAssignedUserId, setFormAssignedUserId] = useState<string>('')

    useEffect(() => {
        if (user?.companyId) {
            loadLeads()
            loadReferenceData()
        }
    }, [user?.companyId, page])

    const loadReferenceData = async () => {
        try {
            const [walletsRes, usersRes, pipelinesRes] = await Promise.all([
                getWallets(),
                user?.companyId
                    ? getUsersByCompany(user.companyId)
                    : Promise.resolve({ success: false, result: [] as any }),
                // `getPipelines` já vem recortado pela visibilidade de funil do perfil, e
                // traz as etapas embutidas — não há consulta por funil escolhido.
                getPipelines(),
            ])
            if (walletsRes.success) setWallets(walletsRes.result || [])
            if (usersRes.success) setCompanyUsers(usersRes.result || [])
            if (pipelinesRes.success) setPipelines(pipelinesRes.result || [])
        } catch {
            // silently fail
        }
    }

    const buildFilters = (): LeadFilters => {
        const filters: LeadFilters = {}
        if (searchTerm.trim()) filters.search = searchTerm.trim()
        if (filterStatus) filters.status = filterStatus as LeadStatus
        if (filterLifecycle)
            filters.lifecycleStatus = filterLifecycle as LeadLifecycleStatus
        if (filterSentiment)
            filters.currentSentiment = filterSentiment as LeadSentimentType
        if (filterTemperature)
            filters.temperature = filterTemperature as LeadTemperature
        if (filterWalletId === 'triage') filters.unassignedOnly = true
        else if (filterWalletId) filters.walletId = parseInt(filterWalletId)
        if (filterPipelineId) filters.pipelineId = parseInt(filterPipelineId)
        if (filterStageId) filters.stageId = parseInt(filterStageId)
        if (filterMinScore) filters.minScore = parseInt(filterMinScore)
        if (filterMaxScore) filters.maxScore = parseInt(filterMaxScore)
        if (filterStartDate) filters.startDate = filterStartDate
        if (filterEndDate) filters.endDate = filterEndDate
        if (filterTags.trim()) filters.tags = filterTags.trim()
        return filters
    }

    const loadLeads = async () => {
        setLoading(true)
        try {
            const filters = buildFilters()
            const response = await getLeads({ ...filters, page, limit: LIMIT })
            if (response.success && response.result) {
                setLeads(response.result.items)
                setTotalLeads(response.result.total)
            } else {
                toast.error(response.error || 'Erro ao carregar leads')
            }
        } catch {
            toast.error('Erro ao carregar leads')
        } finally {
            setLoading(false)
        }
    }

    const handleSearch = () => {
        loadLeads()
    }

    const handleClearFilters = () => {
        setSearchTerm('')
        setFilterStatus('')
        setFilterLifecycle('')
        setFilterSentiment('')
        setFilterTemperature('')
        setFilterWalletId('')
        setFilterPipelineId('')
        setFilterStageId('')
        setFilterMinScore('')
        setFilterMaxScore('')
        setFilterStartDate('')
        setFilterEndDate('')
        setFilterTags('')
    }

    // Reset pagina ao trocar filtros (e reload via useEffect dependente de `page`).
    useEffect(() => {
        if (!loading) {
            const timer = setTimeout(() => {
                if (page !== 1) setPage(1)
                else loadLeads()
            }, 400)
            return () => clearTimeout(timer)
        }
    }, [
        filterStatus,
        filterLifecycle,
        filterSentiment,
        filterTemperature,
        filterWalletId,
        filterPipelineId,
        filterStageId,
        filterMinScore,
        filterMaxScore,
        filterStartDate,
        filterEndDate,
        filterTags,
    ])

    useEffect(() => {
        if (!loading) {
            const timer = setTimeout(() => {
                if (page !== 1) setPage(1)
                else loadLeads()
            }, 500)
            return () => clearTimeout(timer)
        }
    }, [searchTerm])

    /**
     * Etapas do funil escolhido. Listar as etapas de TODOS os funis seria uma lista com
     * nomes repetidos ("Contato", "Proposta") sem dizer de qual funil é cada uma.
     */
    const filteredStages =
        pipelines
            .find(pipeline => String(pipeline.id) === filterPipelineId)
            ?.stages?.slice()
            .sort((a, b) => a.sortOrder - b.sortOrder) ?? []

    const hasActiveFilters =
        filterStatus ||
        filterLifecycle ||
        filterSentiment ||
        filterTemperature ||
        filterWalletId ||
        filterPipelineId ||
        filterStageId ||
        filterMinScore ||
        filterMaxScore ||
        filterStartDate ||
        filterEndDate ||
        filterTags

    const openCreateModal = () => {
        setFormName('')
        setFormPhone('')
        setFormEmail('')
        setFormSource('')
        setFormLifecycle('')
        setFormTemperature('')
        setFormHasBudget(false)
        setFormIsDecisionMaker(false)
        setFormHasNeed(false)
        setFormExpectedTimeline('')
        setFormUtmSource('')
        setFormUtmMedium('')
        setFormUtmCampaign('')
        setFormUtmTerm('')
        setFormUtmContent('')
        setFormCompanyName('')
        setFormPosition('')
        setFormWebsite('')
        setFormTags('')
        setFormWalletId('')
        setFormAssignedUserId('')
        setShowCreateModal(true)
    }

    const handleCreate = async () => {
        if (!formName.trim()) {
            toast.error('O nome é obrigatório')
            return
        }
        if (!formPhone.trim()) {
            toast.error('O telefone é obrigatório')
            return
        }

        setCreating(true)
        try {
            const data: CreateLeadData = {
                name: formName.trim(),
                phoneNumber: formPhone.trim(),
                email: formEmail.trim() || undefined,
                source: (formSource as LeadSource) || undefined,
                lifecycleStatus:
                    (formLifecycle as LeadLifecycleStatus) || undefined,
                temperature: (formTemperature as LeadTemperature) || undefined,
                hasBudget: formHasBudget || undefined,
                isDecisionMaker: formIsDecisionMaker || undefined,
                hasNeed: formHasNeed || undefined,
                expectedTimeline: formExpectedTimeline.trim() || undefined,
                utmSource: formUtmSource.trim() || undefined,
                utmMedium: formUtmMedium.trim() || undefined,
                utmCampaign: formUtmCampaign.trim() || undefined,
                utmTerm: formUtmTerm.trim() || undefined,
                utmContent: formUtmContent.trim() || undefined,
                leadCompanyName: formCompanyName.trim() || undefined,
                position: formPosition.trim() || undefined,
                leadWebsite: formWebsite.trim() || undefined,
                tags: formTags.trim()
                    ? formTags
                          .split(',')
                          .map(t => t.trim())
                          .filter(Boolean)
                    : undefined,
                assignedUserId: formAssignedUserId
                    ? parseInt(formAssignedUserId)
                    : undefined,
            }

            const response = await createLead(data)
            if (response.success) {
                toast.success('Lead criado com sucesso')
                setShowCreateModal(false)
                loadLeads()
            } else {
                toast.error(response.error || 'Erro ao criar lead')
            }
        } catch {
            toast.error('Erro ao criar lead')
        } finally {
            setCreating(false)
        }
    }

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        })
    }

    if (loading && leads.length === 0) {
        return (
            <div className="flex flex-col gap-6 p-6">
                <div className="flex items-center gap-3">
                    <div className="skeleton w-10 h-10 rounded-xl" />
                    <div>
                        <div className="skeleton h-6 w-40 mb-1" />
                        <div className="skeleton h-4 w-28" />
                    </div>
                </div>
                <div className="skeleton h-12 w-full rounded-lg" />
                <div className="space-y-2">
                    {[1, 2, 3, 4, 5].map(i => (
                        <div
                            key={i}
                            className="skeleton h-16 w-full rounded-lg"
                        />
                    ))}
                </div>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 p-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdPerson className="text-2xl text-white" />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className="text-2xl font-bold text-base-content">
                                Leads
                            </h1>
                            <span className="badge badge-sm badge-secondary">
                                {totalLeads}
                            </span>
                        </div>
                        <p className="text-sm text-base-content/60">
                            Gerencie seus leads e oportunidades
                        </p>
                    </div>
                </div>
                {canCreate && (
                    <button
                        className="btn btn-secondary gap-2"
                        onClick={openCreateModal}
                    >
                        <MdAdd className="text-xl" />
                        <span className="hidden sm:inline">Criar Lead</span>
                    </button>
                )}
            </div>

            {/* Search + Filters */}
            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4 space-y-3">
                <div className="flex items-center gap-2">
                    <div className="flex-1 relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none z-10" />
                        <input
                            type="text"
                            placeholder="Buscar por nome, telefone ou e-mail..."
                            className="input input-bordered w-full pl-10"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleSearch()}
                        />
                    </div>
                    {searchTerm && (
                        <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => setSearchTerm('')}
                        >
                            <MdClose className="text-lg" />
                        </button>
                    )}
                    <button
                        className={`btn btn-sm gap-1 ${showFilters || hasActiveFilters ? 'btn-secondary' : 'btn-ghost'}`}
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        <MdFilterList className="text-lg" />
                        Filtros
                        {hasActiveFilters && (
                            <span className="badge badge-xs badge-accent">
                                !
                            </span>
                        )}
                    </button>
                </div>

                {/* Filter Panel */}
                {showFilters && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 pt-3 border-t border-base-300/50">
                        <div className="form-control">
                            <label className="label py-0">
                                <span className="label-text text-xs">
                                    Status
                                </span>
                            </label>
                            <select
                                className="select select-bordered select-sm w-full"
                                value={filterStatus}
                                onChange={e => setFilterStatus(e.target.value)}
                            >
                                <option value="">Todos</option>
                                {(
                                    Object.keys(
                                        LEAD_STATUS_LABELS
                                    ) as LeadStatus[]
                                ).map(k => (
                                    <option key={k} value={k}>
                                        {LEAD_STATUS_LABELS[k]}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-control">
                            <label className="label py-0">
                                <span className="label-text text-xs">
                                    Ciclo de Vida
                                </span>
                            </label>
                            <select
                                className="select select-bordered select-sm w-full"
                                value={filterLifecycle}
                                onChange={e =>
                                    setFilterLifecycle(e.target.value)
                                }
                            >
                                <option value="">Todos</option>
                                {(
                                    Object.keys(
                                        LEAD_LIFECYCLE_LABELS
                                    ) as LeadLifecycleStatus[]
                                ).map(k => (
                                    <option key={k} value={k}>
                                        {LEAD_LIFECYCLE_LABELS[k]}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-control">
                            <label className="label py-0">
                                <span className="label-text text-xs">
                                    Temperatura
                                </span>
                            </label>
                            <select
                                className="select select-bordered select-sm w-full"
                                value={filterTemperature}
                                onChange={e =>
                                    setFilterTemperature(e.target.value)
                                }
                            >
                                <option value="">Todas</option>
                                {(
                                    Object.keys(
                                        LEAD_TEMPERATURE_LABELS
                                    ) as LeadTemperature[]
                                ).map(k => (
                                    <option key={k} value={k}>
                                        {LEAD_TEMPERATURE_LABELS[k]}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* A carteira é do cliente: o filtro atravessa o
                            cliente vinculado. "Em triagem" são os leads que
                            ainda não viraram cliente e por isso não têm carteira. */}
                        <div className="form-control">
                            <label className="label py-0">
                                <span className="label-text text-xs">
                                    Carteira do cliente
                                </span>
                            </label>
                            <select
                                className="select select-bordered select-sm w-full"
                                value={filterWalletId}
                                onChange={e =>
                                    setFilterWalletId(e.target.value)
                                }
                            >
                                <option value="">Todas</option>
                                <option value="triage">Em triagem (sem cliente)</option>
                                {wallets.map(w => (
                                    <option key={w.id} value={w.id}>
                                        {w.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Funil e etapa são da OPORTUNIDADE, não do lead: o filtro
                            traz quem tem ao menos uma oportunidade ali. */}
                        <div className="form-control">
                            <label className="label py-0">
                                <span className="label-text text-xs">
                                    Funil
                                </span>
                            </label>
                            <select
                                className="select select-bordered select-sm w-full"
                                value={filterPipelineId}
                                onChange={e => {
                                    setFilterPipelineId(e.target.value)
                                    // A etapa pertence ao funil anterior: mantê-la
                                    // selecionada cruzaria funil com etapa de outro e
                                    // devolveria lista vazia sem explicação.
                                    setFilterStageId('')
                                }}
                            >
                                <option value="">Todos</option>
                                {pipelines.map(pipeline => (
                                    <option key={pipeline.id} value={pipeline.id}>
                                        {pipeline.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-control">
                            <label className="label py-0">
                                <span className="label-text text-xs">
                                    Etapa
                                </span>
                            </label>
                            <select
                                className="select select-bordered select-sm w-full"
                                value={filterStageId}
                                onChange={e => setFilterStageId(e.target.value)}
                                disabled={!filterPipelineId}
                            >
                                <option value="">
                                    {filterPipelineId
                                        ? 'Todas'
                                        : 'Escolha o funil'}
                                </option>
                                {filteredStages.map(stage => (
                                    <option key={stage.id} value={stage.id}>
                                        {stage.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-control">
                            <label className="label py-0">
                                <span className="label-text text-xs">
                                    Score mínimo
                                </span>
                            </label>
                            <input
                                type="number"
                                className="input input-bordered input-sm w-full"
                                min={0}
                                max={100}
                                placeholder="0"
                                value={filterMinScore}
                                onChange={e =>
                                    setFilterMinScore(e.target.value)
                                }
                            />
                        </div>

                        <div className="form-control">
                            <label className="label py-0">
                                <span className="label-text text-xs">
                                    Score máximo
                                </span>
                            </label>
                            <input
                                type="number"
                                className="input input-bordered input-sm w-full"
                                min={0}
                                max={100}
                                placeholder="100"
                                value={filterMaxScore}
                                onChange={e =>
                                    setFilterMaxScore(e.target.value)
                                }
                            />
                        </div>

                        <div className="form-control">
                            <label className="label py-0">
                                <span className="label-text text-xs">
                                    Data início
                                </span>
                            </label>
                            <input
                                type="date"
                                className="input input-bordered input-sm w-full"
                                value={filterStartDate}
                                onChange={e =>
                                    setFilterStartDate(e.target.value)
                                }
                            />
                        </div>

                        <div className="form-control">
                            <label className="label py-0">
                                <span className="label-text text-xs">
                                    Data fim
                                </span>
                            </label>
                            <input
                                type="date"
                                className="input input-bordered input-sm w-full"
                                value={filterEndDate}
                                onChange={e => setFilterEndDate(e.target.value)}
                            />
                        </div>

                        <div className="form-control">
                            <label className="label py-0">
                                <span className="label-text text-xs">Tags</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered input-sm w-full"
                                placeholder="tag1, tag2"
                                value={filterTags}
                                onChange={e => setFilterTags(e.target.value)}
                            />
                        </div>

                        {hasActiveFilters && (
                            <div className="flex items-end col-span-2 sm:col-span-1">
                                <button
                                    className="btn btn-ghost btn-sm text-error gap-1 w-full"
                                    onClick={() => {
                                        handleClearFilters()
                                    }}
                                >
                                    <MdClear className="text-base" />
                                    Limpar filtros
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Leads */}
            {leads.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdPerson className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            {hasActiveFilters || searchTerm
                                ? 'Nenhum lead encontrado'
                                : 'Nenhum lead cadastrado'}
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            {hasActiveFilters || searchTerm
                                ? 'Tente ajustar seus filtros de busca'
                                : 'Comece adicionando leads ao sistema de vendas'}
                        </p>
                        {!hasActiveFilters && !searchTerm && canCreate && (
                            <button
                                onClick={openCreateModal}
                                className="btn btn-secondary gap-2"
                            >
                                <MdAdd className="text-xl" />
                                Criar primeiro lead
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <>
                    <div className="space-y-3">
                        {leads.map(lead => (
                            <div
                                key={lead.id}
                                className="card bg-base-100 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                                onClick={() =>
                                    navigate(`/sales/leads/${lead.id}`)
                                }
                            >
                                <div className="card-body p-4">
                                    {/* Header */}
                                    <div className="flex items-start justify-between">
                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                            <MdPerson
                                                size={20}
                                                className="text-secondary shrink-0"
                                            />
                                            <div className="min-w-0">
                                                <h3 className="font-semibold truncate">
                                                    {lead.name}
                                                </h3>
                                                <p className="text-sm opacity-60 truncate">
                                                    {lead.phoneNumber}
                                                </p>
                                            </div>
                                        </div>
                                        <span
                                            className={`badge badge-sm ${LEAD_STATUS_COLORS[lead.status]}`}
                                        >
                                            {LEAD_STATUS_LABELS[lead.status]}
                                        </span>
                                    </div>

                                    {/* Badges */}
                                    <div className="flex flex-wrap gap-2 mt-2">
                                        {lead.isMQL && (
                                            <span className="badge badge-xs badge-info">
                                                MQL
                                            </span>
                                        )}
                                        {lead.isSAL && (
                                            <span className="badge badge-xs badge-warning">
                                                SAL
                                            </span>
                                        )}
                                        {lead.isSQL && (
                                            <span className="badge badge-xs badge-success">
                                                SQL
                                            </span>
                                        )}
                                        {lead.temperature && (
                                            <span className="badge badge-sm gap-1">
                                                <span
                                                    className={`w-2 h-2 rounded-full ${TEMPERATURE_DOT[lead.temperature]}`}
                                                />
                                                {
                                                    LEAD_TEMPERATURE_LABELS[
                                                        lead.temperature
                                                    ]
                                                }
                                            </span>
                                        )}
                                        {lead.lifecycleStatus && (
                                            <span className="badge badge-sm badge-outline">
                                                {
                                                    LEAD_LIFECYCLE_LABELS[
                                                        lead.lifecycleStatus
                                                    ]
                                                }
                                            </span>
                                        )}
                                        {lead.currentSentiment && (
                                            <span
                                                className={`badge badge-sm ${SENTIMENT_BG[lead.currentSentiment]}`}
                                            >
                                                {
                                                    LEAD_SENTIMENT_LABELS[
                                                        lead.currentSentiment
                                                    ]
                                                }
                                            </span>
                                        )}
                                        {lead.source && (
                                            <span className="badge badge-sm badge-ghost gap-1">
                                                {(lead.source === 'INSTAGRAM' ||
                                                    lead.source === 'MESSENGER' ||
                                                    lead.source === 'WHATSAPP' ||
                                                    lead.source === 'DEX_TALK') && (
                                                    <ChatChannelIcon channel={lead.source} size={12} />
                                                )}
                                                {LEAD_SOURCE_LABELS[
                                                    lead.source
                                                ] || lead.source}
                                            </span>
                                        )}
                                    </div>

                                    {/* Info */}
                                    <div className="flex items-center gap-4 mt-2 text-xs opacity-60">
                                        {lead.email && (
                                            <span>{lead.email}</span>
                                        )}
                                        {lead.assignedUser && (
                                            <span>
                                                {lead.assignedUser.name}
                                            </span>
                                        )}
                                        {lead.score > 0 && (
                                            <span>Score: {lead.score}</span>
                                        )}
                                        <span>
                                            {formatDate(lead.createdAt)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    {loading && (
                        <div className="flex justify-center py-4">
                            <span className="loading loading-spinner loading-md text-secondary" />
                        </div>
                    )}
                    <PaginationControls
                        page={page}
                        limit={LIMIT}
                        total={totalLeads}
                        loading={loading}
                        onPageChange={setPage}
                        label="lead"
                    />
                </>
            )}

            {/* Create Lead Modal */}
            {showCreateModal && (
                <dialog className="modal modal-open z-50">
                    <div className="modal-box !max-w-3xl max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-6 sticky top-0 bg-base-100 pb-2 z-10">
                            <h3 className="text-lg font-bold">Criar Lead</h3>
                            <button
                                className="btn btn-ghost btn-sm btn-circle"
                                onClick={() => setShowCreateModal(false)}
                            >
                                <MdClose className="text-lg" />
                            </button>
                        </div>

                        <div className="space-y-6">
                            {/* Dados Básicos */}
                            <div>
                                <h4 className="font-semibold text-base-content mb-3 flex items-center gap-2">
                                    <MdPerson className="text-secondary" />
                                    Dados Básicos
                                </h4>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="form-control w-full">
                                        <label className="label">
                                            <span className="label-text font-medium">
                                                Nome *
                                            </span>
                                        </label>
                                        <input
                                            type="text"
                                            className="input input-bordered w-full"
                                            placeholder="Nome do lead"
                                            value={formName}
                                            onChange={e =>
                                                setFormName(e.target.value)
                                            }
                                            autoFocus
                                        />
                                    </div>
                                    <div className="form-control w-full">
                                        <label className="label">
                                            <span className="label-text font-medium">
                                                Telefone *
                                            </span>
                                        </label>
                                        <input
                                            type="tel"
                                            className="input input-bordered w-full"
                                            placeholder="(00) 00000-0000"
                                            value={formPhone}
                                            onChange={e =>
                                                setFormPhone(e.target.value)
                                            }
                                        />
                                    </div>
                                    <div className="form-control w-full">
                                        <label className="label">
                                            <span className="label-text font-medium">
                                                E-mail
                                            </span>
                                        </label>
                                        <input
                                            type="email"
                                            className="input input-bordered w-full"
                                            placeholder="email@exemplo.com"
                                            value={formEmail}
                                            onChange={e =>
                                                setFormEmail(e.target.value)
                                            }
                                        />
                                    </div>
                                    <div className="form-control w-full">
                                        <label className="label">
                                            <span className="label-text font-medium">
                                                Origem
                                            </span>
                                        </label>
                                        <select
                                            className="select select-bordered w-full"
                                            value={formSource}
                                            onChange={e =>
                                                setFormSource(
                                                    e.target.value as LeadSource
                                                )
                                            }
                                        >
                                            <option value="">
                                                Selecione...
                                            </option>
                                            {(
                                                Object.keys(
                                                    LEAD_SOURCE_LABELS
                                                ) as LeadSource[]
                                            ).map(k => (
                                                <option key={k} value={k}>
                                                    {LEAD_SOURCE_LABELS[k]}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Qualificação */}
                            <div>
                                <h4 className="font-semibold text-base-content mb-3 flex items-center gap-2">
                                    <MdLocalOffer className="text-secondary" />
                                    Qualificação
                                </h4>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="form-control w-full">
                                        <label className="label">
                                            <span className="label-text font-medium">
                                                Ciclo de Vida
                                            </span>
                                        </label>
                                        <select
                                            className="select select-bordered w-full"
                                            value={formLifecycle}
                                            onChange={e =>
                                                setFormLifecycle(
                                                    e.target
                                                        .value as LeadLifecycleStatus
                                                )
                                            }
                                        >
                                            <option value="">
                                                Selecione...
                                            </option>
                                            {(
                                                Object.keys(
                                                    LEAD_LIFECYCLE_LABELS
                                                ) as LeadLifecycleStatus[]
                                            ).map(k => (
                                                <option key={k} value={k}>
                                                    {LEAD_LIFECYCLE_LABELS[k]}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-control w-full">
                                        <label className="label">
                                            <span className="label-text font-medium">
                                                Temperatura
                                            </span>
                                        </label>
                                        <select
                                            className="select select-bordered w-full"
                                            value={formTemperature}
                                            onChange={e =>
                                                setFormTemperature(
                                                    e.target
                                                        .value as LeadTemperature
                                                )
                                            }
                                        >
                                            <option value="">
                                                Selecione...
                                            </option>
                                            {(
                                                Object.keys(
                                                    LEAD_TEMPERATURE_LABELS
                                                ) as LeadTemperature[]
                                            ).map(k => (
                                                <option key={k} value={k}>
                                                    {LEAD_TEMPERATURE_LABELS[k]}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-control w-full sm:col-span-2">
                                        <label className="label">
                                            <span className="label-text font-medium">
                                                Prazo Esperado
                                            </span>
                                        </label>
                                        <input
                                            type="text"
                                            className="input input-bordered w-full"
                                            placeholder="Ex: 30 dias, próximo trimestre..."
                                            value={formExpectedTimeline}
                                            onChange={e =>
                                                setFormExpectedTimeline(
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </div>
                                    <div className="sm:col-span-2 flex flex-wrap gap-6">
                                        <label className="label cursor-pointer gap-3">
                                            <input
                                                type="checkbox"
                                                className="checkbox checkbox-secondary checkbox-sm"
                                                checked={formHasBudget}
                                                onChange={e =>
                                                    setFormHasBudget(
                                                        e.target.checked
                                                    )
                                                }
                                            />
                                            <span className="label-text">
                                                Possui orçamento
                                            </span>
                                        </label>
                                        <label className="label cursor-pointer gap-3">
                                            <input
                                                type="checkbox"
                                                className="checkbox checkbox-secondary checkbox-sm"
                                                checked={formIsDecisionMaker}
                                                onChange={e =>
                                                    setFormIsDecisionMaker(
                                                        e.target.checked
                                                    )
                                                }
                                            />
                                            <span className="label-text">
                                                Tomador de decisão
                                            </span>
                                        </label>
                                        <label className="label cursor-pointer gap-3">
                                            <input
                                                type="checkbox"
                                                className="checkbox checkbox-secondary checkbox-sm"
                                                checked={formHasNeed}
                                                onChange={e =>
                                                    setFormHasNeed(
                                                        e.target.checked
                                                    )
                                                }
                                            />
                                            <span className="label-text">
                                                Possui necessidade
                                            </span>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            {/* UTM */}
                            <div>
                                <h4 className="font-semibold text-base-content mb-3 flex items-center gap-2">
                                    <MdSearch className="text-accent" />
                                    UTM / Rastreamento
                                </h4>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                    <div className="form-control w-full">
                                        <label className="label">
                                            <span className="label-text font-medium">
                                                Source
                                            </span>
                                        </label>
                                        <input
                                            type="text"
                                            className="input input-bordered w-full"
                                            placeholder="google, facebook..."
                                            value={formUtmSource}
                                            onChange={e =>
                                                setFormUtmSource(e.target.value)
                                            }
                                        />
                                    </div>
                                    <div className="form-control w-full">
                                        <label className="label">
                                            <span className="label-text font-medium">
                                                Medium
                                            </span>
                                        </label>
                                        <input
                                            type="text"
                                            className="input input-bordered w-full"
                                            placeholder="cpc, organic..."
                                            value={formUtmMedium}
                                            onChange={e =>
                                                setFormUtmMedium(e.target.value)
                                            }
                                        />
                                    </div>
                                    <div className="form-control w-full">
                                        <label className="label">
                                            <span className="label-text font-medium">
                                                Campaign
                                            </span>
                                        </label>
                                        <input
                                            type="text"
                                            className="input input-bordered w-full"
                                            placeholder="Nome da campanha"
                                            value={formUtmCampaign}
                                            onChange={e =>
                                                setFormUtmCampaign(
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </div>
                                    <div className="form-control w-full">
                                        <label className="label">
                                            <span className="label-text font-medium">
                                                Term
                                            </span>
                                        </label>
                                        <input
                                            type="text"
                                            className="input input-bordered w-full"
                                            placeholder="Palavra-chave"
                                            value={formUtmTerm}
                                            onChange={e =>
                                                setFormUtmTerm(e.target.value)
                                            }
                                        />
                                    </div>
                                    <div className="form-control w-full sm:col-span-2">
                                        <label className="label">
                                            <span className="label-text font-medium">
                                                Content
                                            </span>
                                        </label>
                                        <input
                                            type="text"
                                            className="input input-bordered w-full"
                                            placeholder="Conteúdo do anúncio"
                                            value={formUtmContent}
                                            onChange={e =>
                                                setFormUtmContent(
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Extras */}
                            <div>
                                <h4 className="font-semibold text-base-content mb-3 flex items-center gap-2">
                                    <MdLocalOffer className="text-warning" />
                                    Extras
                                </h4>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="form-control w-full">
                                        <label className="label">
                                            <span className="label-text font-medium">
                                                Empresa
                                            </span>
                                        </label>
                                        <input
                                            type="text"
                                            className="input input-bordered w-full"
                                            placeholder="Nome da empresa"
                                            value={formCompanyName}
                                            onChange={e =>
                                                setFormCompanyName(
                                                    e.target.value
                                                )
                                            }
                                        />
                                    </div>
                                    <div className="form-control w-full">
                                        <label className="label">
                                            <span className="label-text font-medium">
                                                Cargo
                                            </span>
                                        </label>
                                        <input
                                            type="text"
                                            className="input input-bordered w-full"
                                            placeholder="Cargo ou função"
                                            value={formPosition}
                                            onChange={e =>
                                                setFormPosition(e.target.value)
                                            }
                                        />
                                    </div>
                                    <div className="form-control w-full">
                                        <label className="label">
                                            <span className="label-text font-medium">
                                                Website
                                            </span>
                                        </label>
                                        <input
                                            type="url"
                                            className="input input-bordered w-full"
                                            placeholder="https://..."
                                            value={formWebsite}
                                            onChange={e =>
                                                setFormWebsite(e.target.value)
                                            }
                                        />
                                    </div>
                                    <div className="form-control w-full">
                                        <label className="label">
                                            <span className="label-text font-medium">
                                                Tags
                                            </span>
                                        </label>
                                        <input
                                            type="text"
                                            className="input input-bordered w-full"
                                            placeholder="tag1, tag2, tag3"
                                            value={formTags}
                                            onChange={e =>
                                                setFormTags(e.target.value)
                                            }
                                        />
                                        <label className="label">
                                            <span className="label-text-alt text-base-content/50">
                                                Separadas por vírgula
                                            </span>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            {/* Atribuição */}
                            <div>
                                <h4 className="font-semibold text-base-content mb-3 flex items-center gap-2">
                                    <MdPerson className="text-info" />
                                    Atribuição
                                </h4>
                                {/* Sem carteira aqui: ela pertence ao cliente e é
                                    definida quando o lead é convertido. */}
                                <div className="grid grid-cols-1 gap-4">
                                    <div className="form-control w-full">
                                        <label className="label">
                                            <span className="label-text font-medium">
                                                Responsável
                                            </span>
                                        </label>
                                        <select
                                            className="select select-bordered w-full"
                                            value={formAssignedUserId}
                                            onChange={e =>
                                                setFormAssignedUserId(
                                                    e.target.value
                                                )
                                            }
                                        >
                                            <option value="">Nenhum</option>
                                            {companyUsers.map(u => (
                                                <option key={u.id} value={u.id}>
                                                    {u.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="modal-action sticky bottom-0 bg-base-100 pt-4">
                            <button
                                className="btn btn-ghost"
                                onClick={() => setShowCreateModal(false)}
                                disabled={creating}
                            >
                                Cancelar
                            </button>
                            <button
                                className="btn btn-secondary"
                                onClick={handleCreate}
                                disabled={
                                    creating ||
                                    !formName.trim() ||
                                    !formPhone.trim()
                                }
                            >
                                {creating ? (
                                    <span className="loading loading-spinner loading-sm" />
                                ) : (
                                    'Criar Lead'
                                )}
                            </button>
                        </div>
                    </div>
                    <form method="dialog" className="modal-backdrop">
                        <button onClick={() => setShowCreateModal(false)}>
                            close
                        </button>
                    </form>
                </dialog>
            )}
        </div>
    )
}
